import { Box, Button, Typography } from '@mui/material';
import { QuestionnaireProps, QuestionnaireQuestion } from './types';
import { InputKit, PhoneNrInput } from '../UI';
import { styles } from './Questionnaire.styles';
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SelectOptions } from './SelectOptions';
import { SelectBoolean } from './SelectBoolean';
import { useMemo, useState } from 'react';
import { TextareaKit } from '../UI/TextareaKit';
import { QuestionType } from '../../Utilis/Types/Task';

const formatTitle = (title: string) => {
  return title
    .split(/\r\n|\r|\n/g)
    .map((title: string) => <span>{title}</span>);
};

export const Questionnaire = ({
  initialData,
  questions,
  validationSchema,
  onChangeStep,
  onQuestionnaireFinish,
  onQuestionnaireQuit,
}: QuestionnaireProps) => {
  const [questionnaireValidation, setQuestionnaireValidation] =
    useState(validationSchema);
  const [questionnaire, setQuestionnaire] =
    useState<QuestionnaireQuestion[]>(questions);
  const methods = useForm({
    defaultValues: initialData,
    resolver: questionnaireValidation
      ? zodResolver(questionnaireValidation)
      : undefined,
  });
  const [page, setPage] = useState(0);

  const currentQuestions: any = useMemo(() => {
    if (!!questionnaire[page].subQuestions?.length) {
      return questionnaire[page].subQuestions;
    } else {
      return [questionnaire[page]];
    }
  }, [questionnaire, page]);

  const addQuestionnaireStep = () => {
    const newQuestionnaire: QuestionnaireQuestion[] = [...questionnaire];
    const newQuestion = structuredClone(questionnaire[page]);
    const test: any = [newQuestion, ...newQuestion.subQuestions!];
    const newSchema: any = {};

    for (const question of test) {
      const name = question.name.split('_');

      question.name =
        name.length === 1
          ? `${name}_1`
          : [name[0], Number(name[1]) + 1].join('_');

      const schemaField = questionnaireValidation?.shape[name.join('_')];

      if (!!schemaField) {
        newSchema[question.name] = schemaField;
      }
    }

    const updatedQuestionnaireValidation =
      questionnaireValidation?.extend(newSchema);
    newQuestionnaire.splice(page + 1, 0, newQuestion);

    setQuestionnaireValidation(updatedQuestionnaireValidation);
    setQuestionnaire(newQuestionnaire);
    onNextStep();
  };

  const removeIncompleteStep = () => {
    const isAddedStep = currentQuestions.some(
      (quest: any) => quest.name.split('_').length > 1
    );

    if (!!isAddedStep) {
      const formValues = methods.getValues();
      const isFormEmpty = currentQuestions.every(
        (section: QuestionnaireQuestion) =>
          !formValues[section.name] || !formValues[section.name].length
      );

      if (!!isFormEmpty) {
        const newQuestionnaire = [...questionnaire];
        newQuestionnaire.splice(page, 1);
        setQuestionnaire(newQuestionnaire);
      }
    }
  };

  const onStepError = (errors: FieldErrors) => {
    const errorFields = Object.keys(errors);

    const isStepInvalid = currentQuestions.some(
      (section: QuestionnaireQuestion) => {
        return errorFields.some(key => key === section.name);
      }
    );

    if (!isStepInvalid) {
      onNextStep(methods.getValues());
    }
  };

  const onDuplicatedError = (errors: FieldErrors) => {
    const errorFields = Object.keys(errors);

    const isStepInvalid = currentQuestions.some(
      (section: QuestionnaireQuestion) => {
        return errorFields.some(key => key === section.name);
      }
    );

    if (!isStepInvalid) {
      addQuestionnaireStep();
    }
  };

  const onNextStep = (values?: FieldValues) => {
    if (page + 1 < questionnaire.length) {
      methods.clearErrors();
      setPage(page + 1);
      onChangeStep(questionnaire[page + 1].stepId);
    } else if (!!values) {
      !!onQuestionnaireFinish &&
        onQuestionnaireFinish({
          questions: questionnaire,
          schema: questionnaireValidation,
          values,
        });
    }
  };

  const onPreviousStep = () => {
    if (page > 0) {
      removeIncompleteStep();
      setPage(page - 1);
      onChangeStep(questionnaire[page - 1].stepId);
    } else {
      !!onQuestionnaireQuit && onQuestionnaireQuit();
    }
  };

  const getContent = (question: QuestionnaireQuestion) => {
    switch (question.type) {
      case QuestionType.INPUT:
      case QuestionType.NUMBER:
        return (
          <InputKit
            name={question.name}
            placeholder={question.placeholder || undefined}
            type={question.type === QuestionType.INPUT ? 'text' : 'number'}
          />
        );
      case QuestionType.SELECT:
      case QuestionType.MULTISELECT:
        return (
          <SelectOptions
            name={question.name}
            values={question.values}
            multi={question.type === QuestionType.MULTISELECT}
          />
        );
      case QuestionType.BOOLEAN:
        return <SelectBoolean name={question.name} />;
      case QuestionType.TEXTAREA:
        return (
          <TextareaKit
            name={question.name}
            placeholder={question.placeholder}
          />
        );
      case QuestionType.PHONE_NUMBER:
        return <PhoneNrInput name={question.name} />;
      case QuestionType.EMAIL:
        return (
          <InputKit name={question.name} placeholder='Email' type='email' />
        );
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        style={styles.formWrapper}
        onSubmit={methods.handleSubmit(onNextStep, onStepError)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
          {!page && (
            <Box>
              <Typography
                variant='h1'
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '48px',
                }}
              >
                {formatTitle("Welcome aboard!\nLet's start with the basics:")}
              </Typography>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                {currentQuestions.map((question: any) => getContent(question))}
              </Box>
            </Box>
          )}
          {page > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
              {currentQuestions.map((question: any) => {
                return (
                  <>
                    <Typography
                      variant='h1'
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      {formatTitle(question.title)}
                    </Typography>
                    {getContent(question)}
                  </>
                );
              })}

              {!!questionnaire[page].duplicable && (
                <Button
                  variant='contained'
                  sx={styles.addPageButton}
                  onClick={methods.handleSubmit(
                    addQuestionnaireStep,
                    onDuplicatedError
                  )}
                >
                  Add another business
                </Button>
              )}
            </Box>
          )}
        </Box>
        <Box sx={styles.paginationButtons}>
          <Button
            variant='outlined'
            style={{
              padding: '13px 32px',
              color: '#655C68',
              borderColor: '#E3DDE6',
            }}
            onClick={onPreviousStep}
          >
            Back
          </Button>
          <Button
            type='submit'
            variant='contained'
            style={{ padding: '13px 32px' }}
          >
            Next
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};
